// The active workspace's plan and quotas, loaded once and shared.
//
// Same reasoning as `useConnections`: the composer, the quick composer and the
// sidebar's plan card all want this answer, and before this hook each of them
// asked for it separately on every navigation. It is cached per workspace and
// re-read on demand — after an upgrade, or after a post is created and the
// month's quota moves.
//
// Everything read from here is a HINT. `create_post` and its triggers enforce
// the plan independently, so a stale entitlement can only ever be less
// permissive than the real answer, never more — which is the safe direction.

import { useCallback, useEffect, useState } from "react";
import { loadEntitlements } from "./entitlements";
import type { Entitlements } from "./types";

const cache = new Map<string, Entitlements>();
const listeners = new Set<(workspaceId: string) => void>();

/** Drop the cached plan so the next reader re-reads it — after a publish, an
 *  upgrade, or anything else that moves a quota. */
export function forgetEntitlements(workspaceId?: string) {
  if (workspaceId) cache.delete(workspaceId);
  else cache.clear();
  for (const notify of listeners) notify(workspaceId ?? "");
}

export interface EntitlementsState {
  entitlements: Entitlements | null;
  /**
   * False until an answer has arrived — including a failed one. Null
   * entitlements mean "no hints", which is different from "not yet", and the
   * two must not be confused by anything that offers to schedule.
   */
  settled: boolean;
  reload: () => Promise<void>;
}

export function useEntitlements(workspaceId: string | null): EntitlementsState {
  const [entitlements, setEntitlements] = useState<Entitlements | null>(
    () => (workspaceId ? cache.get(workspaceId) ?? null : null),
  );
  const [settled, setSettled] = useState(() => !!workspaceId && cache.has(workspaceId));

  const load = useCallback(async (force: boolean) => {
    if (!workspaceId) {
      setEntitlements(null);
      setSettled(false);
      return;
    }
    const hit = cache.get(workspaceId);
    if (hit && !force) {
      setEntitlements(hit);
      setSettled(true);
      return;
    }
    try {
      const ent = await loadEntitlements(workspaceId);
      cache.set(workspaceId, ent);
      setEntitlements(ent);
    } catch {
      // Never let a failed plan read block composing: the database enforces the
      // plan regardless, and a null here just means no hints.
      setEntitlements(null);
    } finally {
      setSettled(true);
    }
  }, [workspaceId]);

  useEffect(() => { void load(false); }, [load]);

  useEffect(() => {
    const notify = (id: string) => {
      if (!workspaceId) return;
      if (id && id !== workspaceId) return;
      void load(true);
    };
    listeners.add(notify);
    return () => { listeners.delete(notify); };
  }, [workspaceId, load]);

  return { entitlements, settled, reload: () => load(true) };
}
